/**
 * Badge — small count / dot indicator for the Mango — Farmer Market kit.
 *
 * Per `preview/chips-badges.html`: a tiny badge anchored to a child (e.g. a
 * notification bell). Defaults to the error role for attention; `color`
 * switches it to leaf (success) or mango (tertiary) container tones.
 *
 * Wraps MUI `Badge`. When `dot` is set, renders a small dot with no count;
 * otherwise renders `count`, capped by `max` (e.g. "9+").
 */
import { Badge as MuiBadge } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, typography } from '../theme/tokens.js';

export type BadgeColor = 'alert' | 'success' | 'tertiary';

export interface BadgeProps {
  /** Element the badge is anchored to (e.g. an Icon). */
  children: ReactNode;
  /** Numeric count. Ignored when `dot` is true. */
  count?: number;
  /** Render as a small dot with no number. */
  dot?: boolean;
  /** Cap the displayed count; values above show as `${max}+`. Default 99. */
  max?: number;
  /** Show the badge even when `count` is 0. */
  showZero?: boolean;
  /** Colour role. Default `alert`. */
  color?: BadgeColor;
  /** Pass-through className for layout composition. */
  className?: string;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { children, count, dot = false, max = 99, showZero = false, color = 'alert', className },
  ref,
) {
  // Mode-aware: solid accent roles tint correctly in both modes. In light they
  // resolve to the same brand values the static map used (error=alert[600],
  // primary=leaf[700], tertiary=mango[500]) with the matching on-* foreground.
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const BADGE_COLOR: Record<BadgeColor, { bg: string; fg: string }> = {
    alert: { bg: sys.error, fg: sys.onError },
    success: { bg: sys.primary, fg: sys.onPrimary },
    tertiary: { bg: sys.tertiary, fg: sys.onTertiary },
  };
  const { bg, fg } = BADGE_COLOR[color];
  return (
    <MuiBadge
      ref={ref}
      className={className}
      badgeContent={dot ? undefined : count}
      variant={dot ? 'dot' : 'standard'}
      max={max}
      showZero={showZero}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: bg,
          color: fg,
          fontFamily: typography.fontFamily,
          fontSize: typography.labelSmall.fontSize,
          fontWeight: 700,
        },
      }}
    >
      {children}
    </MuiBadge>
  );
});
